import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './FileUploader.css';

interface FileUploaderProps {
  onFileSelect: (file: File | null) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  error: string | null;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const PRO_RECOMMEND_SIZE = 200 * 1024; // 200KB

const FileUploader: React.FC<FileUploaderProps> = ({ onFileSelect, onSubmit, error }) => {
  const { t } = useTranslation('create');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showProHint, setShowProHint] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return bytes + ' bytes';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      
      // Check file type
      const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'xlsx' && fileExt !== 'xls') {
        onFileSelect(null);
        return;
      }
      
      if (selectedFile.size > MAX_FILE_SIZE) {
        onFileSelect(null);
        setShowProHint(false);
        onSubmit = async (e) => {
          e.preventDefault();
          throw new Error(t('upload.file_too_large', { size: formatFileSize(selectedFile.size) }));
        };
        return;
      }

      setShowProHint(selectedFile.size > PRO_RECOMMEND_SIZE);
      setFile(selectedFile);
      onFileSelect(selectedFile);
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];

      // Check file type
      const fileExt = droppedFile.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'xlsx' && fileExt !== 'xls') {
        onFileSelect(null);
        return;
      }

      if (droppedFile.size > MAX_FILE_SIZE) {
        onFileSelect(null);
        setShowProHint(false);
        onSubmit = async (e) => {
          e.preventDefault();
          throw new Error(t('upload.file_too_large', { size: formatFileSize(droppedFile.size) }));
        };
        return;
      }

      setShowProHint(droppedFile.size > PRO_RECOMMEND_SIZE);
      setFile(droppedFile);
      onFileSelect(droppedFile);
    }
  };
  
  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  // Handle file upload button click
  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Handle file removal
  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
    setShowProHint(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null);
  };
  
  return (
    <div className="modern-upload-container">
      <div className="upload-header">
        <h2>{t('upload.uploader_title')}</h2>
        <p>{t('upload.uploader_subtitle')}</p>
      </div>
      
      <div 
        ref={dropZoneRef}
        className={`modern-upload-area ${isDragging ? 'dragging' : ''} ${file ? 'file-selected' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={file ? undefined : handleUploadButtonClick}
      >
        <div className="upload-icon-container">
          <div className="upload-icon-circle">
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16V8M12 8L8 12M12 8L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        
        <div className="upload-content">
          <h3>{file ? t('upload.file_ready') : t('upload.click_to_upload')}</h3>
          <p>{file ? t('upload.start_analysis') : t('upload.drop_here')}</p>
          
          {file && (
            <div className="selected-file-info">
              <div className="file-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="file-details">
                <span className="file-name">{file.name}</span>
                <span className="file-size">{formatFileSize(file.size)}</span>
              </div>
              <button 
                className="remove-file-button" 
                title={t('upload.remove_file')}
                onClick={handleRemoveFile}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
        
        <input 
          type="file" 
          id="fileInput" 
          name="file" 
          accept=".xlsx,.xls" 
          className="file-input"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </div>
      
      <div className="supported-formats">
        <span>{t('upload.formats_label')}</span>
        <div className="format-badges">
          <div className="format-badge">XLSX</div>
          <div className="format-badge">XLS</div>
        </div>
        <span className="file-size-limit">{t('upload.max_size_label')}</span>
      </div>

      {showProHint && (
        <div className="pro-hint">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span dangerouslySetInnerHTML={{ __html: t('upload.pro_hint') }} />
        </div>
      )}
      
      {file && (
        <button 
          type="button" 
          className="analyze-button"
          onClick={onSubmit}
        >
          <span className="button-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2"/>
              <path d="M12 8V16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 12L12 16L8 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          {t('upload.analyze_button')}
        </button>
      )}
      
      {error && (
        <div className="error-message">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
